import { useState } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { itemsApi } from '@/services/itemsApi';
import { categoryApi, unitApi, gstRateApi, brandApi } from '@/services/masterDataApi';
import { getCurrencySymbol } from '@/utils/currency';
import {
  X, Upload, Download, AlertCircle, CheckCircle, FileText,
  Edit2, Save, XCircle, ChevronDown, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { ItemFormData, TaxType } from '@/types';
import ConfirmModal from '@/components/common/ConfirmModal';
import SimpleSelect from '@/components/common/SimpleSelect';
import * as XLSX from 'xlsx';

interface ImportItemsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ParsedItem extends ItemFormData {
  row: number;
  errors: string[];
  // Non-blocking issues (e.g. optional master values that didn't match)
  warnings?: string[];
  // Store original values from file for display
  originalCategory?: string;
  originalBrand?: string;
  originalUnit?: string;
  originalGstRate?: string;
  // Editing state
  isEditing?: boolean;
}

export default function ImportItemsModal({ isOpen, onClose }: ImportItemsModalProps) {
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [parsedItems, setParsedItems] = useState<ParsedItem[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Fetch master data for validation
  const { data: categories } = useQuery({
    queryKey: ['categories-all'],
    queryFn: () => categoryApi.getAll({ limit: 1000, isActive: true }),
  });

  const { data: brands } = useQuery({
    queryKey: ['brands-all'],
    queryFn: () => brandApi.getAll({ limit: 1000, isActive: true }),
  });

  const { data: units } = useQuery({
    queryKey: ['units-all'],
    queryFn: () => unitApi.getAll({ limit: 1000, isActive: true }),
  });

  const { data: gstRates } = useQuery({
    queryKey: ['gst-rates-all'],
    queryFn: () => gstRateApi.getAll({ limit: 1000, isActive: true }),
  });

  // Clean item data for API - strip metadata fields
  const cleanItemForImport = (item: ParsedItem): ItemFormData => {
    const {
      row,
      errors,
      warnings,
      originalCategory,
      originalBrand,
      originalUnit,
      originalGstRate,
      isEditing,
      itemCode, // Let backend generate itemCode to avoid conflicts
      ...itemData
    } = item;
    return itemData;
  };

  // Import mutation
  const importMutation = useMutation({
    mutationFn: async (items: ParsedItem[]) => {
      const cleanedItems = items.map(cleanItemForImport);
      const results = await Promise.allSettled(
        cleanedItems.map((item) => itemsApi.createItem(item))
      );
      return results;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['items'] });
    },
  });

  // Update item in the list
  const updateParsedItem = (index: number, updates: Partial<ParsedItem>) => {
    setParsedItems(prev => {
      const newItems = [...prev];
      const item = { ...newItems[index], ...updates };

      // Recalculate errors (blocking) and warnings (non-blocking) after update
      const errors: string[] = [];
      const warnings: string[] = [];

      if (!item.itemName) {
        errors.push('Item Name is required');
      }
      if (item.originalCategory && !item.categoryId) {
        warnings.push(`Category "${item.originalCategory}" not found`);
      }
      if (item.originalBrand && !item.brandId) {
        warnings.push(`Brand "${item.originalBrand}" not found`);
      }
      if (item.originalUnit && !item.unitId) {
        warnings.push(`Unit "${item.originalUnit}" not found`);
      }

      item.errors = errors;
      item.warnings = warnings;
      newItems[index] = item;
      return newItems;
    });
  };

  // Toggle edit mode for a row
  const toggleEditMode = (index: number) => {
    updateParsedItem(index, { isEditing: !parsedItems[index].isEditing });
  };

  const downloadSampleCSV = () => {
    const headers = [
      'Item Name*',
      'Item Code',
      'Barcode',
      'Description',
      'Model Variant',
      'Brand',
      'Model',
      'Category',
      'Unit',
      'Purchase Price',
      'Sales Price',
      'HSN Code',
      'GST Rate (%)',
      'Tax Type'
    ];

    const sampleData = [
      [
        'iPhone 15 Pro',
        'IP15P',
        '1234567890123',
        'Latest iPhone model',
        '256GB',
        'Apple',
        'iPhone 15',
        'Mobile Phones',
        'Piece',
        '80000',
        '95000',
        '85171200',
        '18',
        'CGST_SGST'
      ],
      [
        'Samsung Galaxy S24',
        'SGS24',
        '9876543210123',
        'Flagship Samsung phone',
        '512GB',
        'Samsung',
        'Galaxy S24',
        'Mobile Phones',
        'Piece',
        '75000',
        '88000',
        '85171200',
        '18',
        'CGST_SGST'
      ],
    ];

    const csvContent = [
      headers.join(','),
      ...sampleData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `items_import_sample_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const downloadSampleExcel = () => {
    const headers = [
      'Item Name*',
      'Item Code',
      'Barcode',
      'Description',
      'Model Variant',
      'Brand',
      'Model',
      'Category',
      'Unit',
      'Purchase Price',
      'Sales Price',
      'HSN Code',
      'GST Rate (%)',
      'Tax Type'
    ];

    const sampleData = [
      [
        'iPhone 15 Pro',
        'IP15P',
        '1234567890123',
        'Latest iPhone model',
        '256GB',
        'Apple',
        'iPhone 15',
        'Mobile Phones',
        'Piece',
        80000,
        95000,
        '85171200',
        18,
        'CGST_SGST'
      ],
      [
        'Samsung Galaxy S24',
        'SGS24',
        '9876543210123',
        'Flagship Samsung phone',
        '512GB',
        'Samsung',
        'Galaxy S24',
        'Mobile Phones',
        'Piece',
        75000,
        88000,
        '85171200',
        18,
        'CGST_SGST'
      ],
    ];

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([headers, ...sampleData]);

    ws['!cols'] = [
      { wch: 20 }, { wch: 12 }, { wch: 15 }, { wch: 25 }, { wch: 15 },
      { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 10 }, { wch: 15 },
      { wch: 15 }, { wch: 12 }, { wch: 12 }, { wch: 12 },
    ];

    XLSX.utils.book_append_sheet(wb, ws, 'Items');
    XLSX.writeFile(wb, `items_import_sample_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const parseExcel = (file: File): Promise<ParsedItem[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = e.target?.result;
          const workbook = XLSX.read(data, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];

          if (jsonData.length < 2) {
            resolve([]);
            return;
          }

          const items: ParsedItem[] = [];

          for (let i = 1; i < jsonData.length; i++) {
            const row = jsonData[i];
            if (!row || row.length === 0) continue;

            const errors: string[] = [];
            const warnings: string[] = [];

            const itemName = row[0]?.toString() || '';
            if (!itemName) {
              errors.push('Item Name is required');
            }

            const brandName = row[5]?.toString() || '';
            const brandId = brands?.data.find(
              b => b.name.toLowerCase() === brandName?.toLowerCase()
            )?.id;

            const categoryName = row[7]?.toString() || '';
            const categoryId = categories?.data.find(
              c => c.name.toLowerCase() === categoryName?.toLowerCase()
            )?.id;

            const unitName = row[8]?.toString() || '';
            const unitId = units?.data.find(
              u => u.name.toLowerCase() === unitName?.toLowerCase()
            )?.id;

            const gstRateValue = row[12]?.toString() || '';
            const gstRateId = gstRates?.data.find(
              g => g.rate.toString() === gstRateValue
            )?.id;

            const item: ParsedItem = {
              row: i + 1,
              errors,
              warnings,
              itemName: itemName || '',
              itemCode: row[1]?.toString() || undefined,
              barcode: row[2]?.toString() || undefined,
              description: row[3]?.toString() || undefined,
              modelVariant: row[4]?.toString() || undefined,
              brandId: brandId || undefined,
              categoryId: categoryId || undefined,
              unitId: unitId || undefined,
              purchasePrice: row[9] ? parseFloat(row[9].toString()) : undefined,
              salesPrice: row[10] ? parseFloat(row[10].toString()) : undefined,
              hsnCode: row[11]?.toString() || undefined,
              gstRateId: gstRateId || undefined,
              taxType: row[13]?.toString() === 'IGST' ? TaxType.IGST : TaxType.CGST_SGST,
              // Store original values
              originalCategory: categoryName || undefined,
              originalBrand: brandName || undefined,
              originalUnit: unitName || undefined,
              originalGstRate: gstRateValue || undefined,
              isEditing: false,
            };

            if (brandName && !brandId) {
              warnings.push(`Brand "${brandName}" not found`);
            }
            if (categoryName && !categoryId) {
              warnings.push(`Category "${categoryName}" not found`);
            }
            if (unitName && !unitId) {
              warnings.push(`Unit "${unitName}" not found`);
            }

            items.push(item);
          }

          resolve(items);
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = () => reject(reader.error);
      reader.readAsBinaryString(file);
    });
  };

  const parseCSV = (content: string): ParsedItem[] => {
    const lines = content.split('\n').filter(line => line.trim());
    if (lines.length < 2) return [];

    const items: ParsedItem[] = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
      const errors: string[] = [];
      const warnings: string[] = [];

      const itemName = values[0];
      if (!itemName) {
        errors.push('Item Name is required');
      }

      const brandName = values[5];
      const brandId = brands?.data.find(
        b => b.name.toLowerCase() === brandName?.toLowerCase()
      )?.id;

      const categoryName = values[7];
      const categoryId = categories?.data.find(
        c => c.name.toLowerCase() === categoryName?.toLowerCase()
      )?.id;

      const unitName = values[8];
      const unitId = units?.data.find(
        u => u.name.toLowerCase() === unitName?.toLowerCase()
      )?.id;

      const gstRateValue = values[12];
      const gstRateId = gstRates?.data.find(
        g => g.rate.toString() === gstRateValue
      )?.id;

      const item: ParsedItem = {
        row: i + 1,
        errors,
        warnings,
        itemName: itemName || '',
        itemCode: values[1] || undefined,
        barcode: values[2] || undefined,
        description: values[3] || undefined,
        modelVariant: values[4] || undefined,
        brandId: brandId || undefined,
        categoryId: categoryId || undefined,
        unitId: unitId || undefined,
        purchasePrice: values[9] ? parseFloat(values[9]) : undefined,
        salesPrice: values[10] ? parseFloat(values[10]) : undefined,
        hsnCode: values[11] || undefined,
        gstRateId: gstRateId || undefined,
        taxType: values[13] === 'IGST' ? TaxType.IGST : TaxType.CGST_SGST,
        // Store original values
        originalCategory: categoryName || undefined,
        originalBrand: brandName || undefined,
        originalUnit: unitName || undefined,
        originalGstRate: gstRateValue || undefined,
        isEditing: false,
      };

      if (brandName && !brandId) {
        warnings.push(`Brand "${brandName}" not found`);
      }
      if (categoryName && !categoryId) {
        warnings.push(`Category "${categoryName}" not found`);
      }
      if (unitName && !unitId) {
        warnings.push(`Unit "${unitName}" not found`);
      }

      items.push(item);
    }

    return items;
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    const isCSV = selectedFile.name.endsWith('.csv');
    const isExcel = selectedFile.name.endsWith('.xlsx') || selectedFile.name.endsWith('.xls');

    if (!isCSV && !isExcel) {
      toast.error('Please upload a CSV or Excel file');
      return;
    }

    setFile(selectedFile);
    setIsProcessing(true);

    try {
      if (isCSV) {
        const reader = new FileReader();
        reader.onload = (event) => {
          const content = event.target?.result as string;
          const parsed = parseCSV(content);
          setParsedItems(parsed);
          setIsProcessing(false);
        };
        reader.readAsText(selectedFile);
      } else if (isExcel) {
        const parsed = await parseExcel(selectedFile);
        setParsedItems(parsed);
        setIsProcessing(false);
      }
    } catch (error) {
      console.error('Error parsing file:', error);
      toast.error('Error parsing file. Please check the file format.');
      setIsProcessing(false);
    }
  };

  const handleImportClick = () => {
    const validItems = parsedItems.filter(item => item.errors.length === 0);

    if (validItems.length === 0) {
      toast.error('No valid items to import. Please fix the errors first.');
      return;
    }

    setShowConfirmModal(true);
  };

  const handleConfirmImport = async () => {
    const validItems = parsedItems.filter(item => item.errors.length === 0);
    setShowConfirmModal(false);
    setIsProcessing(true);

    const results = await importMutation.mutateAsync(validItems as ParsedItem[]);

    const successCount = results.filter(r => r.status === 'fulfilled').length;
    const failedCount = results.filter(r => r.status === 'rejected').length;

    if (failedCount > 0) {
      toast.info(`Import completed. Success: ${successCount}, Failed: ${failedCount}`);
    } else {
      toast.success(`Successfully imported ${successCount} items`);
    }

    if (successCount > 0) {
      onClose();
    }
    setIsProcessing(false);
  };

  // Importable = no blocking errors (unmatched optional masters are warnings, not errors)
  const validItemsCount = parsedItems.filter(item => item.errors.length === 0).length;
  const invalidItemsCount = parsedItems.length - validItemsCount;
  const needsMappingCount = parsedItems.filter(
    item => item.errors.length === 0 && (item.warnings?.length ?? 0) > 0
  ).length;
  const readyCount = validItemsCount - needsMappingCount;

  if (!isOpen) return null;

  // Inline Dropdown Component
  const MasterDataDropdown = ({
    item,
    index,
    field,
    originalValue,
    currentId,
    options,
    placeholder
  }: {
    item: ParsedItem;
    index: number;
    field: 'categoryId' | 'brandId' | 'unitId' | 'gstRateId';
    originalValue?: string;
    currentId?: string;
    options: { id: string; name: string; rate?: number }[];
    placeholder: string;
  }) => {
    const hasError = originalValue && !currentId;
    const isResolved = originalValue && currentId;

    return (
      <div className="space-y-1">
        <SimpleSelect
          value={currentId || ''}
          onChange={(v) => updateParsedItem(index, { [field]: v || undefined })}
          options={[
            { value: '', label: placeholder },
            ...options.map((opt) => ({
              value: opt.id,
              label: `${opt.name}${opt.rate !== undefined ? ` (${opt.rate}%)` : ''}`,
            })),
          ]}
          placeholder={placeholder}
          size="sm"
          aria-label={placeholder}
          wrapperClassName="w-full min-w-0"
          className={`!rounded-md focus:!ring-blue-500 focus:!border-blue-500 ${
            hasError
              ? '!border-amber-400 !bg-amber-50'
              : isResolved
                ? '!border-green-400 !bg-green-50'
                : ''
          }`}
        />
        {hasError && originalValue && (
          <div className="text-xs text-amber-600 flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" />
            "{originalValue}" not found
          </div>
        )}
        {isResolved && (
          <div className="text-xs text-green-600 flex items-center gap-1">
            <CheckCircle className="h-3 w-3" />
            Mapped
          </div>
        )}
      </div>
    );
  };

  // Inline Edit Input Component
  const InlineEditInput = ({
    value,
    onChange,
    type = 'text',
    placeholder,
    disabled = false,
    className = ''
  }: {
    value: string | number | undefined;
    onChange: (val: string) => void;
    type?: 'text' | 'number';
    placeholder?: string;
    disabled?: boolean;
    className?: string;
  }) => (
    <input
      type={type}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      className={`w-full text-sm px-2 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 ${className}`}
    />
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-7xl w-full max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="border-b border-gray-200 px-6 py-4 flex justify-between items-center bg-white">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Import Items</h2>
            <p className="text-sm text-gray-600 mt-1">Upload a CSV or Excel file to bulk import items</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={isProcessing}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Download Sample */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <FileText className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-blue-900">Need a template?</h3>
                <p className="text-sm text-blue-700 mt-1">
                  Download our sample file to see the correct format
                </p>
                <ul className="text-xs text-blue-700 mt-2 list-disc list-inside space-y-0.5">
                  <li>The <strong>Model</strong> column is not currently imported.</li>
                  <li><strong>Item Code</strong> is auto-generated if left blank.</li>
                </ul>
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={downloadSampleCSV}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                  >
                    <Download className="h-4 w-4" />
                    Download CSV
                  </button>
                  <button
                    onClick={downloadSampleExcel}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                  >
                    <Download className="h-4 w-4" />
                    Download Excel
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload CSV or Excel File
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileChange}
                className="hidden"
                id="file-upload"
                disabled={isProcessing}
              />
              <label htmlFor="file-upload" className="cursor-pointer flex flex-col items-center">
                <Upload className="h-12 w-12 text-gray-400 mb-3" />
                <span className="text-sm font-medium text-gray-700">
                  Click to upload CSV or Excel file
                </span>
                <span className="text-xs text-gray-500 mt-1">
                  Supports .csv, .xlsx formats
                </span>
              </label>
            </div>
            {file && (
              <p className="text-sm text-gray-600 mt-2">
                Selected file: <span className="font-medium">{file.name}</span>
              </p>
            )}
          </div>

          {/* Preview with Inline Editing */}
          {parsedItems.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-900">Preview & Edit</h3>
                <div className="flex gap-4 text-sm">
                  <span className="flex items-center gap-1 text-green-600">
                    <CheckCircle className="h-4 w-4" />
                    {readyCount} Ready
                  </span>
                  {needsMappingCount > 0 && (
                    <span className="flex items-center gap-1 text-amber-600">
                      <AlertTriangle className="h-4 w-4" />
                      {needsMappingCount} Needs mapping (optional)
                    </span>
                  )}
                  {invalidItemsCount > 0 && (
                    <span className="flex items-center gap-1 text-red-600">
                      <AlertCircle className="h-4 w-4" />
                      {invalidItemsCount} Invalid
                    </span>
                  )}
                </div>
              </div>

              {/* Help Text */}
              {needsMappingCount > 0 && (
                <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-800">
                      <strong>Optional:</strong> Some items reference categories, brands, or units that don't match your existing data.
                      These fields are optional, so these items will still import without them. Use the dropdowns below to map them, or click the edit icon to modify other fields.
                    </div>
                  </div>
                </div>
              )}

              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="max-h-[400px] overflow-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-12">Row</th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[180px]">Item Name</th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[180px]">Category</th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[160px]">Brand</th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[120px]">Unit</th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-28">Sales Price</th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-24">Status</th>
                        <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-20">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {parsedItems.map((item, idx) => {
                        const hasErrors = item.errors.length > 0;
                        const needsMapping = (item.warnings?.length ?? 0) > 0;
                        const isEditing = item.isEditing;

                        return (
                          <tr
                            key={idx}
                            className={`${
                              hasErrors
                                ? 'bg-red-50 hover:bg-red-100'
                                : needsMapping
                                  ? 'bg-amber-50 hover:bg-amber-100'
                                  : 'hover:bg-gray-50'
                            } transition-colors`}
                          >
                            <td className="px-3 py-3 text-gray-500 font-medium">{item.row}</td>

                            {/* Item Name */}
                            <td className="px-3 py-3">
                              {isEditing ? (
                                <InlineEditInput
                                  value={item.itemName}
                                  onChange={(val) => updateParsedItem(idx, { itemName: val })}
                                  placeholder="Item name"
                                />
                              ) : (
                                <div className="font-medium text-gray-900">{item.itemName || '-'}</div>
                              )}
                            </td>

                            {/* Category Dropdown */}
                            <td className="px-3 py-3">
                              <MasterDataDropdown
                                item={item}
                                index={idx}
                                field="categoryId"
                                originalValue={item.originalCategory}
                                currentId={item.categoryId}
                                options={categories?.data || []}
                                placeholder="Select Category"
                              />
                            </td>

                            {/* Brand Dropdown */}
                            <td className="px-3 py-3">
                              <MasterDataDropdown
                                item={item}
                                index={idx}
                                field="brandId"
                                originalValue={item.originalBrand}
                                currentId={item.brandId}
                                options={brands?.data || []}
                                placeholder="Select Brand"
                              />
                            </td>

                            {/* Unit Dropdown */}
                            <td className="px-3 py-3">
                              <MasterDataDropdown
                                item={item}
                                index={idx}
                                field="unitId"
                                originalValue={item.originalUnit}
                                currentId={item.unitId}
                                options={units?.data || []}
                                placeholder="Select Unit"
                              />
                            </td>

                            {/* Sales Price */}
                            <td className="px-3 py-3">
                              {isEditing ? (
                                <InlineEditInput
                                  type="number"
                                  value={item.salesPrice}
                                  onChange={(val) => updateParsedItem(idx, { salesPrice: val ? parseFloat(val) : undefined })}
                                  placeholder="0"
                                />
                              ) : (
                                <span className="text-gray-900">
                                  {item.salesPrice ? `${getCurrencySymbol()}${item.salesPrice.toLocaleString()}` : '-'}
                                </span>
                              )}
                            </td>

                            {/* Status */}
                            <td className="px-3 py-3">
                              {hasErrors ? (
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                                  <XCircle className="h-3 w-3" />
                                  Error
                                </span>
                              ) : needsMapping ? (
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                                  <AlertTriangle className="h-3 w-3" />
                                  Needs mapping (optional)
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                  <CheckCircle className="h-3 w-3" />
                                  Ready
                                </span>
                              )}
                            </td>

                            {/* Actions */}
                            <td className="px-3 py-3 text-center">
                              <button
                                onClick={() => toggleEditMode(idx)}
                                className={`p-1.5 rounded-md transition-colors ${
                                  isEditing
                                    ? 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                                    : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                                }`}
                                title={isEditing ? 'Done editing' : 'Edit row'}
                              >
                                {isEditing ? <Save className="h-4 w-4" /> : <Edit2 className="h-4 w-4" />}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 flex justify-between items-center bg-gray-50">
          <div className="text-sm text-gray-600">
            {parsedItems.length > 0 && (
              <span>
                {validItemsCount} of {parsedItems.length} items ready to import
              </span>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={isProcessing}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleImportClick}
              disabled={isProcessing || validItemsCount === 0}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isProcessing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  Processing...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" />
                  Import {validItemsCount} Items
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmImport}
        title="Confirm Import"
        message={`Import ${validItemsCount} items? This action cannot be undone.`}
        confirmLabel="Import"
        variant="info"
        isLoading={isProcessing}
      />
    </div>
  );
}
